/**
 * Правка строки книги ЕТС прямо на экране (AK-772, ADR-073 D11/D12).
 *
 * Founder 28.08 дословно: «сделай правку расценок на экране». До этого лист
 * дерева был read-only и объяснял себя строкой «правит финансист в своём
 * файле» — теперь правит экономист здесь, а книга финансистов остаётся
 * источником ПЕРВОЙ заливки.
 *
 * Два слоя правки, и это не прихоть вёрстки:
 *
 *  • **прямо в таблице** — расценка ₽/га и топливо л/га. Это те две цифры, ради
 *    которых экономист вообще открывает лист; ради них гонять его в диалог
 *    значит превратить одну правку в три клика и потерять возможность
 *    сравнивать соседние строки глазами;
 *  • **диалог «Изменить строку»** — остальной белый список (нормы за смену,
 *    проценты, оплата за норму, единицы, тип операции). Они правятся редко, их
 *    десять, и десять полей ввода в строке таблицы — нечитаемая простыня.
 *
 * Чего здесь нет и не будет: `source_workbook` / `source_sheet` / `source_row` /
 * `work_name_raw`. Это АДРЕС строки в книге. Бэк отвечает на них 422
 * (`extra='forbid'`), а экран не показывает их полем ввода вовсе — поле,
 * которое всегда отказывает, хуже отсутствующего.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatRuDate } from '@/lib/dateFmt';
import { parsePriceInput } from '@/lib/planning/money';
import { useMeasurementUnits } from '@/lib/planning/budgetCatalog';
import { useCompanyOpTypes } from '@/lib/reference/companyOperationTypes';
import {
  NORM_FIELD_LABELS,
  useArchiveNorm,
  useNormDetail,
  usePatchNorm,
  type NormPatch,
  type NormsBookRow,
} from '@/lib/planning/laborNorms';

/** Значение «не выбрано» для Select: пустая строка в Radix запрещена. */
const NONE = '__none__';

/**
 * Подсказка над меткой «изменено вручную»: ЧТО правлено, кем и когда.
 *
 * Названия полей — человеческие («оплата за единицу»), те же, что уходят в лог
 * справочника. Печатать `total_pay_per_unit` в подсказке нельзя: имя колонки —
 * термин модели, а не язык агронома.
 */
export function overriddenTitle(row: NormsBookRow): string {
  const fields = (row.overridden_fields ?? [])
    .map((field) => NORM_FIELD_LABELS[field] ?? field)
    .join(', ');
  const who = row.updated_by ? `, ${row.updated_by}` : '';
  const when = row.updated_at ? `, ${formatRuDate(row.updated_at)}` : '';
  const what = fields ? `правлено вручную: ${fields}` : 'строка правлена вручную';
  return `${what}${who}${when}. Перезаливка книги финансистов эти поля не затирает.`;
}

/** Правлено ли ИМЕННО это поле — подсвечиваем ячейку, а не всю строку. */
export function isFieldOverridden(row: NormsBookRow, field: string): boolean {
  return !!row.is_overridden && (row.overridden_fields ?? []).includes(field);
}

/**
 * Ячейка-число с правкой по месту.
 *
 * Поведение снято с ценовой ячейки бюджета (`BudgetItemsTable`) — тот же жест
 * на соседнем экране обязан работать одинаково: фокус открывает сырое число,
 * блюр сохраняет, мусор остаётся в поле и подсвечивается (а не откатывается
 * молча — человек должен видеть, что именно он набрал), отказ бэка возвращает
 * прежнее значение, чтобы несохранённая цифра не читалась как сохранённая.
 */
export function NormNumberCell({
  value,
  label,
  format,
  overridden,
  disabled,
  onCommit,
}: {
  value: number | null;
  label: string;
  format: (v: number | null) => string;
  overridden: boolean;
  disabled?: boolean;
  onCommit: (next: number | null) => Promise<unknown>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  // «То, что мы только что сохранили» — иначе между ответом сервера и
  // перечиткой дерева ячейка на миг показывает СТАРОЕ число (инвалидация лишь
  // помечает запрос устаревшим, рефетч приезжает отдельно). Тот же приём и по
  // той же причине, что в ценовой ячейке бюджета.
  const [justSaved, setJustSaved] = useState<{ shown: number | null; from: number | null } | null>(
    null,
  );

  useEffect(() => {
    if (justSaved && value !== justSaved.from) setJustSaved(null);
  }, [value, justSaved]);

  if (disabled) {
    return <span className="tabular-nums">{format(value)}</span>;
  }

  const shown = justSaved ? justSaved.shown : value;
  const raw = (v: number | null) => (v === null ? '' : String(v).replace('.', ','));
  return (
    <Input
      ref={inputRef}
      value={editing ? draft : format(shown)}
      inputMode="decimal"
      aria-label={label}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-8 w-24 text-right tabular-nums',
        // Подсветка — на ЯЧЕЙКЕ, а не на строке: правлена расценка, топливо
        // осталось книжным, и красить обе цифры одинаково значит соврать о том,
        // что именно расходится с источником.
        overridden && 'border-accent-brand bg-accent-brand/10',
        invalid && 'border-destructive',
      )}
      onFocus={() => {
        setEditing(true);
        setJustSaved(null);
        setDraft(raw(value));
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const parsed = parsePriceInput(draft);
        if (parsed === undefined) {
          setInvalid(true);
          return;
        }
        setInvalid(false);
        if (parsed === value) {
          setEditing(false);
          return;
        }
        const from = value;
        void onCommit(parsed)
          .then(() => {
            setJustSaved({ shown: parsed, from });
            // Человек мог вернуться в поле и набирать заново, пока ответ на
            // ПРЕЖНИЙ PATCH ещё летел — не выбиваем его из нового ввода.
            if (document.activeElement !== inputRef.current) setEditing(false);
          })
          .catch(() => {
            setDraft(raw(value));
            if (document.activeElement !== inputRef.current) setEditing(false);
          });
      }}
    />
  );
}

/** Числовое поле диалога. */
function NumberField({
  field,
  value,
  onChange,
  overridden,
}: {
  field: string;
  value: string;
  onChange: (next: string) => void;
  overridden: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground" htmlFor={`norm-${field}`}>
        {NORM_FIELD_LABELS[field]}
      </Label>
      <Input
        id={`norm-${field}`}
        value={value}
        inputMode="decimal"
        aria-label={NORM_FIELD_LABELS[field]}
        className={cn('h-8 tabular-nums', overridden && 'border-accent-brand bg-accent-brand/10')}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const DIALOG_NUMBERS = [
  'norm_per_shift',
  'norm_per_shift_t',
  'total_pay_per_unit',
  'total_pay_per_norm',
  'fuel_rate',
  'hazard_pct',
  'variable_pct',
  'sprout_bonus_pct',
] as const;

const DIALOG_REFS = ['pay_unit_id', 'fuel_unit_id', 'company_op_type_id'] as const;

/**
 * Что реально изменилось — только это и уходит в тело PATCH.
 *
 * Слать форму целиком нельзя: бэк помечает `overridden_fields` по ПРИСЛАННЫМ
 * полям, и «правка расценки» полной формой заперла бы от книги все десять полей
 * строки, включая те, которых человек не касался (ADR-073 D12).
 */
export function diffNumbers(
  drafts: Record<string, string>,
  original: Record<string, number | null | undefined>,
): { patch: NormPatch; invalid: string[] } {
  const patch: NormPatch = {};
  const invalid: string[] = [];
  for (const field of DIALOG_NUMBERS) {
    const raw = drafts[field];
    if (raw === undefined) continue;
    const parsed = parsePriceInput(raw);
    if (parsed === undefined) {
      invalid.push(field);
      continue;
    }
    const before = original[field] ?? null;
    if (parsed !== before) (patch as Record<string, unknown>)[field] = parsed;
  }
  return { patch, invalid };
}

/** Диалог «Изменить строку» — остальной белый список полей. */
export function NormEditDialog({
  normId,
  seasonId,
  onClose,
}: {
  normId: string;
  seasonId: string;
  onClose: () => void;
}) {
  const detail = useNormDetail(normId);
  const patch = usePatchNorm(seasonId);
  const units = useMeasurementUnits();
  const opTypes = useCompanyOpTypes();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [invalid, setInvalid] = useState<string[]>([]);

  const data = detail.data;
  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const field of DIALOG_NUMBERS) {
      const value = data[field];
      next[field] = value === null || value === undefined ? '' : String(value).replace('.', ',');
    }
    setDrafts(next);
    setRefs({
      pay_unit_id: data.pay_unit_id ?? NONE,
      fuel_unit_id: data.fuel_unit_id ?? NONE,
      company_op_type_id: data.company_op_type_id ?? NONE,
    });
  }, [data]);

  const overridden = useMemo(() => new Set(data?.overridden_fields ?? []), [data]);
  const unitOptions = useMemo(
    () => [...(units.data ?? [])].sort((a, b) => a.symbol.localeCompare(b.symbol, 'ru')),
    [units.data],
  );
  const opTypeOptions = useMemo(
    () => [...(opTypes.data ?? [])].sort((a, b) => a.local_name.localeCompare(b.local_name, 'ru')),
    [opTypes.data],
  );

  const save = () => {
    if (!data) return;
    const { patch: body, invalid: bad } = diffNumbers(drafts, data as never);
    setInvalid(bad);
    if (bad.length) return;
    for (const field of DIALOG_REFS) {
      const picked = refs[field] === NONE ? null : refs[field];
      if (picked !== (data[field] ?? null)) (body as Record<string, unknown>)[field] = picked;
    }
    // Ничего не изменилось — закрываем без запроса: пустая правка пометила бы
    // строку ручной от одного открытия формы.
    if (!Object.keys(body).length) {
      onClose();
      return;
    }
    void patch
      .mutateAsync({ id: normId, patch: body })
      .then(onClose)
      .catch(() => undefined);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Изменить строку книги</DialogTitle>
          <DialogDescription>
            {data
              ? // Адрес строки в книге — рядом, но только для чтения: он не
                // содержимое строки, а её место в источнике.
                `${data.work_name_raw}${data.params_raw ? `, ${data.params_raw}` : ''} · ${data.source_sheet}, строка ${data.source_row}`
              : 'Читаем строку…'}
          </DialogDescription>
        </DialogHeader>

        {detail.isError && (
          <p className="text-sm text-destructive">
            Не удалось прочитать строку: {detail.error.message}
          </p>
        )}

        {data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {DIALOG_NUMBERS.map((field) => (
              <NumberField
                key={field}
                field={field}
                value={drafts[field] ?? ''}
                overridden={overridden.has(field)}
                onChange={(next) => setDrafts((cur) => ({ ...cur, [field]: next }))}
              />
            ))}

            {(['pay_unit_id', 'fuel_unit_id'] as const).map((field) => (
              <div key={field} className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">{NORM_FIELD_LABELS[field]}</Label>
                <Select
                  value={refs[field] ?? NONE}
                  onValueChange={(v) => setRefs((cur) => ({ ...cur, [field]: v }))}
                >
                  <SelectTrigger className="h-8" aria-label={NORM_FIELD_LABELS[field]}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>не указана</SelectItem>
                    {unitOptions.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.symbol}
                        {unit.name_ru ? ` — ${unit.name_ru}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="col-span-2 flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">
                {NORM_FIELD_LABELS.company_op_type_id}
              </Label>
              <Select
                value={refs.company_op_type_id ?? NONE}
                onValueChange={(v) => setRefs((cur) => ({ ...cur, company_op_type_id: v }))}
              >
                <SelectTrigger className="h-8" aria-label={NORM_FIELD_LABELS.company_op_type_id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opTypeOptions.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.local_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {invalid.length > 0 && (
          <p className="text-sm text-destructive">
            Не число: {invalid.map((f) => NORM_FIELD_LABELS[f] ?? f).join(', ')}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={save} disabled={!data || patch.isPending}>
            {patch.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Подтверждение архива. Именно подтверждение, а не «сделали, вот отмена»: под
 * узлом дерева строки похожи одна на другую (тот же трактор, соседняя ширина),
 * и промах мышью здесь стоит строки книги.
 */
export function ArchiveNormDialog({
  row,
  seasonId,
  onClose,
}: {
  row: NormsBookRow;
  seasonId: string;
  onClose: () => void;
}) {
  const archive = useArchiveNorm(seasonId);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Убрать строку из книги?</DialogTitle>
          <DialogDescription>
            {row.prime_mover ?? '—'}
            {row.implement ? ` · ${row.implement}` : ''}
            {row.source_sheet ? ` · ${row.source_sheet}, строка ${row.source_row}` : ''}. Строка
            уйдёт в архив: она перестанет попадать в расчёты и в дерево, но останется в системе —
            на неё ссылаются роли, правила оплаты и прошлые расчёты.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            disabled={archive.isPending}
            onClick={() =>
              void archive
                .mutateAsync({ id: row.id })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            {archive.isPending ? 'Убираем…' : 'Убрать в архив'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Меню строки: полная правка и архив. */
export function NormRowMenu({
  row,
  onEdit,
  onArchive,
}: {
  row: NormsBookRow;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const what = `${row.prime_mover ?? '—'}${row.implement ? ` · ${row.implement}` : ''}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={`Меню строки — ${what}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />
          Изменить строку…
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onArchive}>
          <Archive className="mr-2 h-4 w-4" />
          Убрать строку в архив…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Метка «изменено вручную» — рядом с цифрами, а не в карточке строки. */
export function OverriddenBadge({ row }: { row: NormsBookRow }) {
  if (!row.is_overridden) return null;
  return (
    <Badge variant="outline" className="ml-2 border-accent-brand" title={overriddenTitle(row)}>
      изменено вручную
    </Badge>
  );
}
